import { useState, useEffect, useCallback, useRef } from "react";
import { useBoardScroll } from "./BoardContext";

interface MinimapState {
  visible: boolean;
  viewportLeft: number;
  viewportWidth: number;
}

export function useBoardMinimap() {
  const { scrollRef } = useBoardScroll();
  const [state, setState] = useState<MinimapState>({
    visible: false,
    viewportLeft: 0,
    viewportWidth: 1,
  });
  const rafRef = useRef<number>(0);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    const overflows = scrollWidth > clientWidth + 1;
    setState({
      visible: overflows,
      viewportLeft: overflows ? scrollLeft / scrollWidth : 0,
      viewportWidth: overflows ? clientWidth / scrollWidth : 1,
    });
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);

    // Also observe children size changes (columns added/removed)
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });

    update();

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      mo.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [scrollRef, update]);

  const onMinimapClick = useCallback(
    (fractionX: number) => {
      const el = scrollRef.current;
      if (!el) return;
      const targetCenter = fractionX * el.scrollWidth;
      el.scrollTo({
        left: targetCenter - el.clientWidth / 2,
        behavior: "smooth",
      });
    },
    [scrollRef],
  );

  const onViewportDrag = useCallback(
    (deltaFraction: number) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollLeft += deltaFraction * el.scrollWidth;
    },
    [scrollRef],
  );

  return {
    visible: state.visible,
    viewportLeft: state.viewportLeft,
    viewportWidth: state.viewportWidth,
    onMinimapClick,
    onViewportDrag,
  };
}
